import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '../../../redux/editorSlice';
import { ImageSliderProps, ImageItem } from './ImageSlider.types';
import { adaptImageSliderProps } from './ImageSlider.adapter';
import { useTranslation } from '../../../redux/TranslationHelper';
import './ImageSlider.css';

/**
 * 이미지 슬라이더 컴포넌트
 */
const ImageSliderComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptImageSliderProps(props);
  const { style = {}, className = '', id, data = {} } = adaptedProps;
  
  // 데이터 추출
  const {
    sliderTitle = '이미지 슬라이더',
    showTitle = false,
    titleFontSize = '24px',
    titleFontWeight = '600',
    titleColor = '#333',
    sliderHeight = '400px',
    slidesToShow = 1,
    autoSlide = true,
    autoSlideInterval = 4000,
    infiniteSlide = true,
    showNavigation = true,
    showIndicators = true,
    imageObjectFit = 'cover',
    images = []
  } = data;

  // Redux hooks
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  // 상태 변수들
  const [currentIndex, setCurrentIndex] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // 참조 변수들
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoSlideTimer = useRef<NodeJS.Timeout | null>(null);
  const swipeThreshold = 50;
  
  // 무한 슬라이드를 위한 이미지 배열 생성
  const extendedImages = useMemo(() => {
    if (!images || images.length === 0) return [];
    if (!infiniteSlide) return images;
    
    // 무한 슬라이드를 위해 앞뒤에 이미지 추가
    const lastImages = images.slice(-slidesToShow);
    const firstImages = images.slice(0, slidesToShow);
    return [...lastImages, ...images, ...firstImages];
  }, [images, infiniteSlide, slidesToShow]);
  
  // 타이틀 스타일 객체
  const titleStyle = useMemo(() => ({
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    color: titleColor
  }), [titleFontSize, titleFontWeight, titleColor]);
  
  // 슬라이더 컨테이너 스타일
  const sliderContainerStyle = useMemo(() => {
    // 패딩 관련 스타일을 제외한 스타일만 적용 (wrapper에서 이미 패딩이 적용되므로)
    const filteredStyle = { ...style };
    delete filteredStyle.padding;
    delete filteredStyle.paddingTop;
    delete filteredStyle.paddingRight;
    delete filteredStyle.paddingBottom;
    delete filteredStyle.paddingLeft;
    
    return {
      height: sliderHeight,
      background: 'transparent',
      backgroundColor: 'transparent',
      ...filteredStyle
    };
  }, [sliderHeight, style]);
  
  // 슬라이더 스타일 - translateX 픽셀 기반으로 변경
  const sliderStyle = useMemo(() => ({
    transform: `translateX(${translateX}px)`,
    transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
    display: 'flex',
    width: 'auto'
  }), [translateX, isTransitioning]);

  // 다음 슬라이드로 이동
  const goToNext = useCallback(() => {
    if (!extendedImages || extendedImages.length === 0 || slideWidth === 0) return;
    
    setTranslateX(prev => prev - slideWidth);
    setCurrentIndex(prev => prev + 1);
  }, [extendedImages, slideWidth]);

  // 이전 슬라이드로 이동
  const goToPrev = useCallback(() => {
    if (!extendedImages || extendedImages.length === 0 || slideWidth === 0) return;
    
    setTranslateX(prev => prev + slideWidth);
    setCurrentIndex(prev => prev - 1);
  }, [extendedImages, slideWidth]);

  // 특정 슬라이드로 이동
  const goToSlide = useCallback((index: number) => {
    if (slideWidth === 0) return;
    
    if (!infiniteSlide) {
      setTranslateX(-index * slideWidth);
      setCurrentIndex(index);
    } else {
      const targetIndex = index + slidesToShow;
      setTranslateX(-targetIndex * slideWidth);
      setCurrentIndex(targetIndex);
    }
  }, [infiniteSlide, slidesToShow, slideWidth]);

  // 자동 슬라이드 시작
  const startAutoSlide = useCallback(() => {
    if (autoSlide && autoSlideInterval > 0) {
      autoSlideTimer.current = setInterval(() => {
        goToNext();
      }, autoSlideInterval);
    }
  }, [autoSlide, autoSlideInterval, goToNext]);

  // 자동 슬라이드 중지
  const stopAutoSlide = useCallback(() => {
    if (autoSlideTimer.current) {
      clearInterval(autoSlideTimer.current);
      autoSlideTimer.current = null;
    }
  }, []);

  // 무한 슬라이드 경계 처리
  useEffect(() => {
    if (!infiniteSlide || !extendedImages || extendedImages.length === 0 || slideWidth === 0) return;
    
    const handleTransitionEnd = () => {
      if (currentIndex <= 0) {
        // 첫 번째 복제본에 도달 시 마지막 원본으로 이동
        setIsTransitioning(false);
        const newIndex = images.length;
        setCurrentIndex(newIndex);
        setTranslateX(-newIndex * slideWidth);
      } else if (currentIndex >= images.length + slidesToShow) {
        // 마지막 복제본에 도달 시 첫 번째 원본으로 이동
        setIsTransitioning(false);
        const newIndex = slidesToShow;
        setCurrentIndex(newIndex);
        setTranslateX(-newIndex * slideWidth);
      }
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('transitionend', handleTransitionEnd);
      return () => slider.removeEventListener('transitionend', handleTransitionEnd);
    }
  }, [currentIndex, extendedImages, images, infiniteSlide, slidesToShow, slideWidth]);

  // 트랜지션 재활성화
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  // 자동 슬라이드 관리
  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
  }, [startAutoSlide, stopAutoSlide]);

  // 터치 이벤트 핸들러
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
    stopAutoSlide();
  }, [stopAutoSlide]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      startAutoSlide();
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > swipeThreshold;
    const isRightSwipe = distance < -swipeThreshold;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
    startAutoSlide();
  }, [touchStart, touchEnd, goToNext, goToPrev, startAutoSlide, swipeThreshold]);

  // 마우스 이벤트 핸들러 (자동 슬라이드 일시 정지)
  const handleMouseEnter = useCallback(() => {
    stopAutoSlide();
  }, [stopAutoSlide]);

  const handleMouseLeave = useCallback(() => {
    startAutoSlide();
  }, [startAutoSlide]);

  // 타이틀 편집 핸들러
  const handleTitleDoubleClick = useCallback(() => {
    setIsEditingTitle(true);
    setTitleEditValue(sliderTitle);
  }, [sliderTitle]);
  
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    
    dispatch(updateComponent({
      id,
      changes: {
        componentProps: {
          ...data,
          sliderTitle: titleEditValue
        }
      }
    }));
  }, [id, data, dispatch, titleEditValue]);

  // 이미지 클릭 핸들러
  const handleImageClick = useCallback((image: ImageItem) => {
    if (image.link) {
      if (image.openInNewTab) {
        window.open(image.link, '_blank');
      } else {
        window.location.href = image.link;
      }
    }
  }, []);

  // 화면 크기 감지
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    
    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    
    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  // 슬라이드 너비 계산
  useEffect(() => {
    if (containerRef.current && extendedImages.length > 0) {
      const calculateWidths = () => {
        try {
          // 컨테이너의 실제 패딩값 가져오기
          const containerStyles = window.getComputedStyle(containerRef.current!);
          const paddingLeft = parseFloat(containerStyles.paddingLeft);
          const paddingRight = parseFloat(containerStyles.paddingRight);
          
          // 컨테이너의 내부 너비 (패딩 제외)
          const containerWidth = containerRef.current!.offsetWidth;
          const innerWidth = containerWidth - paddingLeft - paddingRight;
          
          // 디바이스별 표시 개수 결정
          let displayCount = slidesToShow;
          if (isMobile) {
            displayCount = Math.min(slidesToShow, 1);
          } else if (isTablet) {
            displayCount = Math.min(slidesToShow, 2);
          }
          
          // 정확한 슬라이드 너비 계산
          const exactSlideWidth = innerWidth / displayCount;
          setSlideWidth(exactSlideWidth);
          
          // 초기 위치 설정
          if (infiniteSlide) {
            setCurrentIndex(slidesToShow);
            setTranslateX(-exactSlideWidth * slidesToShow);
          } else {
            setCurrentIndex(0);
            setTranslateX(0);
          }
        } catch (error) {
          console.error('슬라이드 너비 계산 오류:', error);
        }
      };
      
      calculateWidths();
      window.addEventListener('resize', calculateWidths);
      
      return () => {
        window.removeEventListener('resize', calculateWidths);
      };
    }
  }, [containerRef, extendedImages.length, slidesToShow, isMobile, isTablet, infiniteSlide]);

  // 이미지가 없는 경우
  if (!extendedImages || extendedImages.length === 0) {
    return (
      <div 
        className={`image-slider-container empty ${className}`} 
        style={sliderContainerStyle}
        ref={containerRef}
      >
        {showTitle && (
          <div className="image-slider-header">
            {isEditingTitle ? (
              <input
                type="text"
                value={titleEditValue}
                onChange={(e) => setTitleEditValue(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyPress={(e) => e.key === 'Enter' && handleTitleBlur()}
                style={titleStyle}
                className="title-edit-input"
                autoFocus
              />
            ) : (
              <h2
                className="image-slider-title"
                style={titleStyle}
                onDoubleClick={handleTitleDoubleClick}
              >
                {sliderTitle}
              </h2>
            )}
          </div>
        )}
        <div className="image-slider-empty">
          <p>{t('표시할 이미지가 없습니다.')}</p>
          <p>{t('속성 패널에서 이미지를 추가해주세요.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`image-slider-container ${className}`}
      style={sliderContainerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
    >
      {/* 제목 */}
      {showTitle && (
        <div className="image-slider-header">
          {isEditingTitle ? (
            <input
              type="text"
              value={titleEditValue}
              onChange={(e) => setTitleEditValue(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyPress={(e) => e.key === 'Enter' && handleTitleBlur()}
              style={titleStyle}
              className="title-edit-input"
              autoFocus
            />
          ) : (
            <h2
              className="image-slider-title"
              style={titleStyle}
              onDoubleClick={handleTitleDoubleClick}
            >
              {sliderTitle}
            </h2>
          )}
        </div>
      )}

      {/* 슬라이더 메인 영역 */}
      <div className="image-slider-main">
        <div 
          className="image-slider-viewport"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            ref={sliderRef}
            className="image-slider-track"
            style={sliderStyle}
          >
            {extendedImages.map((image: any, index: number) => (
              <div 
                key={`${image.id}-${index}`} 
                className="image-slider-slide"
                style={{ width: slideWidth > 0 ? `${slideWidth}px` : 'auto' }}
              >
                <div
                  className="image-slider-image-container"
                  onClick={() => handleImageClick(image)}
                  style={{ cursor: image.link ? 'pointer' : 'default' }}
                >
                  <img
                    src={image.url}
                    alt={image.alt || image.title || `슬라이드 ${index + 1}`}
                    className="image-slider-image"
                    style={{ objectFit: imageObjectFit }}
                    loading="lazy"
                  />
                  {image.title && (
                    <div className="image-slider-image-overlay">
                      <h3 className="image-slider-image-title">{image.title}</h3>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 네비게이션 버튼 */}
        {showNavigation && extendedImages.length > 1 && (
          <>
            <button
              className="image-slider-nav image-slider-nav-prev"
              onClick={goToPrev}
              aria-label="이전 이미지"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className="image-slider-nav image-slider-nav-next"
              onClick={goToNext}
              aria-label="다음 이미지"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}

        {/* 인디케이터 */}
        {showIndicators && images.length > 1 && (
          <div className="image-slider-indicators">
            {images.map((_: any, index: number) => {
              const currentSlideIndex = infiniteSlide 
                ? Math.max(0, Math.min(images.length - 1, currentIndex - slidesToShow))
                : currentIndex;
              
              return (
                <button
                  key={index}
                  className={`image-slider-indicator ${
                    currentSlideIndex === index ? 'active' : ''
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`${index + 1}번째 이미지로 이동`}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageSliderComponent; 